import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Button,
    Divider,
} from '@mui/material';
import {
    Tv as TwitchIcon,
    CalendarToday as CalendarIcon,
    Casino as GameIcon,
} from '@mui/icons-material';

const MaghlaProject: React.FC = () => {
    return (
        <Box>
            {/* Header avec titre et image */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <img
                    src="/Twitch/RefMenyaLogo.png"
                    alt="Logo Maghla Games"
                    style={{
                        width: '64px',
                        height: '64px',
                        objectFit: 'contain',
                        filter: 'drop-shadow(2px 2px 4px rgba(167, 139, 250, 0.3))',
                    }}
                />
                <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold'
                    }}
                >
                    Les jeux de Maghla
                </Typography>
            </Box>

            {/* Statut et date */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                        2024 - En cours
                    </Typography>
                </Box>
            </Box>

            {/* Description */}
            <Typography
                variant="body1"
                sx={{ mb: 3, lineHeight: 1.6 }}
            >
                Suite complète de jeux interactifs développée pour la streameuse Maghla.
                Ensemble de mini-jeux utilisant les points de chaîne Twitch et l'interaction
                avec le chat pour créer une expérience de stream engageante et communautaire.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Jeux d'attente */}
            <Box sx={{
                mb: 3,
                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.05) 0%, rgba(20, 184, 166, 0.05) 100%)',
                border: '1px solid rgba(167, 139, 250, 0.2)',
                borderRadius: 2,
                p: 2,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <GameIcon sx={{ color: '#a78bfa' }} />
                    <Typography
                        variant="h6"
                        sx={{
                            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontWeight: 'bold'
                        }}
                    >
                        Jeux d'attente
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Les refs de Meinya :</strong> Jeu de type "pendu" où les viewers votent
                    pour deviner les lettres d'une référence. Vote toutes les 30 secondes avec système de vies.
                </Typography>
                <Typography variant="body2">
                    <strong>Devine chien code :</strong> Jeu sémantique où les viewers proposent des mots
                    pour trouver le mot mystère, avec scoring de 0 à 1000 selon la proximité sémantique.
                </Typography>
            </Box>

            {/* Points de chaîne */}
            <Box sx={{
                mb: 3,
                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.05) 0%, rgba(20, 184, 166, 0.05) 100%)',
                border: '1px solid rgba(167, 139, 250, 0.2)',
                borderRadius: 2,
                p: 2,
            }}>
                <Typography
                    variant="h6"
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold',
                        mb: 2
                    }}
                >
                    Jeux avec points de chaîne
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Sub Or Ban :</strong> Jeu risqué où les viewers dépensent leurs points pour
                    tenter de gagner un abonnement... ou se faire bannir temporairement !
                </Typography>
                <Typography variant="body2">
                    <strong>Attrape Meinya :</strong> Mini-jeu de réaction où Meinya apparaît brièvement
                    à l'écran et les viewers doivent l'attraper en premier.
                </Typography>
            </Box>

            {/* Fonctionnalités principales */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold'
                    }}
                >
                    Fonctionnalités principales
                </Typography>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2
                }}>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • 4 jeux interactifs distincts
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Système de classement persistant
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Intégration points de chaîne Twitch
                        </Typography>

                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Interface streamer intuitive
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Événements temps réel
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            • Gestion automatique des bans/subs
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Technologies */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 'bold'
                    }}
                >
                    Technologies utilisées
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['TypeScript', 'Express', 'React', 'Twitch API', 'Twitch Events', 'WebSocket', 'MongoDB'].map((tech) => (
                        <Chip
                            key={tech}
                            label={tech}
                            variant="filled"
                            size="small"
                            sx={{
                                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(20, 184, 166, 0.1) 100%)',
                                color: '#a78bfa',
                                border: '1px solid rgba(167, 139, 250, 0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2) 0%, rgba(20, 184, 166, 0.2) 100%)',
                                },
                            }}
                        />
                    ))}
                </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Liens actions */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    startIcon={<TwitchIcon />}
                    href="https://twitch.tv/maghla"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #0f766e 100%)',
                        },
                    }}
                >
                    Voir sur Twitch
                </Button>
            </Box>
        </Box>
    );
};

export default MaghlaProject;

